"use client"; // This component needs to be a Client Component

import posthog from "posthog-js"; // Use the initialized client-side instance
import { Component, ErrorInfo, ReactNode } from "react";

interface IProps {
  children?: ReactNode;
}

interface IState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends Component<IProps, IState> {
  constructor(props: IProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): IState {
    // Update state so the next render will show the fallback UI.
    return { hasError: true, error: error };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error details
    console.error("ErrorBoundary caught an error:", error, errorInfo);

    // Capture the exception with PostHog
    // Check if posthog is initialized before using it
    if (posthog?.__loaded) {
      // Use a check like this or ensure it's always loaded
      posthog.captureException(error, { componentStack: errorInfo.componentStack });
    }
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <h1>Something went wrong.</h1>
          <p>We&apos;ve been notified and are looking into it.</p>
          {/* Optionally display error details in development */}
          {process.env.NODE_ENV === "development" && this.state.error && (
            <pre
              style={{
                marginTop: "20px",
                textAlign: "left",
                background: "#f0f0f0",
                padding: "10px",
                borderRadius: "5px",
                overflowX: "auto"
              }}
            >
              {this.state.error.toString()}
              {this.state.error.stack}
            </pre>
          )}
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
